import React from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { 
  Calculator,
  ArrowRight,
  CheckCircle,
  Shield,
  Phone,
  Droplets,
  Flame,
  Sparkles,
  Building2
} from 'lucide-react';
import { Link } from 'react-router-dom';

const PolymerPage = () => {
  const features = [
    { text: 'Certified Non Toxic', icon: Sparkles },
    { text: 'EU REACH Compliant', icon: Shield },
    { text: 'Flame Retardant', icon: Flame },
    { text: '10 Year Structural Warranty', icon: Shield },
    { text: 'Made From Virgin Material, Not Recycled', icon: Sparkles },
    { text: 'Suitable for Commercial Environments', icon: Building2 },
    { text: 'Maintenance Free & Easy Cleaning', icon: Sparkles },
    { text: 'Full hidden tilt option available', icon: CheckCircle },
    { text: '100% Waterproof', icon: Droplets },
    { text: 'Will not warp, crack or fade', icon: Shield }
  ];

  const bestFor = [
    'Bathrooms & En-suites',
    'Kitchens',
    'Utility rooms',
    'Commercial properties',
    'Swimming pool areas',
    'Coastal homes'
  ];

  const faqs = [
    {
      question: 'Are Polymer Shutters completely waterproof?',
      answer: 'Yes, our Polymer Shutters offer market-leading water resistance. They are 100% waterproof and will not warp, crack, or deteriorate when exposed to moisture, making them perfect for bathrooms, kitchens, and other wet environments.'
    },
    {
      question: 'What is "hidden tilt" and why is it special?',
      answer: 'Hidden tilt means the tilt rod mechanism is concealed within the shutter panels, giving a cleaner, more streamlined appearance. This premium feature is rare in the industry and not offered by most shutter installers. It provides a sleek, modern look without visible tilt rods.'
    },
    {
      question: 'Are Polymer Shutters safe for homes with children?',
      answer: 'Absolutely. Our Polymer Shutters are certified non-toxic and EU REACH compliant, meeting the strictest safety standards. They are made from virgin material and are completely safe for family homes.'
    },
    {
      question: 'How do I clean Polymer Shutters?',
      answer: 'Cleaning is incredibly easy - simply wipe down with a damp cloth. The waterproof nature means you can use cleaning solutions without worry, and they will not absorb moisture or harbour mould.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-20" style={{background: 'linear-gradient(to bottom right, #e8f4ec, #d4e8dc)'}}>
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6" style={{backgroundColor: 'rgba(140, 184, 159, 0.2)'}}>
                <Droplets className="h-5 w-5" style={{color: '#8CB89F'}} />
                <Badge style={{backgroundColor: '#8CB89F'}} className="text-white">Waterproof Range</Badge>
              </div>
              
              <h1 className="text-5xl font-bold text-gray-900 mb-6">
                Polymer Shutters
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Market-leading water resistance with optional full hidden tilt - a rare benefit not offered by most 
                shutter installers. Perfect for bathrooms, kitchens, and any high-moisture environment.
              </p>
              
              <div className="flex flex-wrap gap-4 mb-8">
                <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm">
                  <Shield className="h-5 w-5" style={{color: '#8CB89F'}} />
                  <span className="font-semibold text-gray-900">10 Year Warranty</span>
                </div>
                <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm">
                  <Droplets className="h-5 w-5" style={{color: '#8CB89F'}} />
                  <span className="font-semibold text-gray-900">100% Waterproof</span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/calculator">
                  <Button size="lg" className="text-white px-8 py-4 text-lg" style={{backgroundColor: '#8CB89F'}}>
                    <Calculator className="mr-2 h-5 w-5" />
                    Use Our Online Estimator
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button size="lg" variant="outline" className="px-8 py-4 text-lg" style={{borderColor: '#8CB89F', color: '#8CB89F'}}>
                    <Phone className="mr-2 h-5 w-5" />
                    Book Free Survey
                  </Button>
                </Link>
              </div>
            </div>
            
            <div className="relative">
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/b91ea611-2364-4d55-8415-0e2be2797319/images/128a6a6620e3f91b5c428df7e5edbce10d4f071a1b206bcb32d695f1a1f3a456.png"
                alt="Polymer Shutters"
                className="w-full h-auto rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Key Benefits Banner */}
      <section className="py-8" style={{backgroundColor: '#8CB89F'}}>
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-white text-center">
            <div className="flex flex-col items-center gap-2">
              <Droplets className="h-8 w-8" />
              <span className="font-semibold">100% Waterproof</span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <Flame className="h-8 w-8" />
              <span className="font-semibold">Flame Retardant</span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <Sparkles className="h-8 w-8" />
              <span className="font-semibold">Non-Toxic</span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <Shield className="h-8 w-8" />
              <span className="font-semibold">10 Year Warranty</span>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Premium Features</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Our Polymer Shutters are engineered for performance in challenging environments.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-2 rounded-lg" style={{backgroundColor: 'rgba(140, 184, 159, 0.1)'}}>
                      <feature.icon className="h-6 w-6" style={{color: '#8CB89F'}} />
                    </div>
                    <span className="text-gray-700 font-medium pt-2">{feature.text}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Best For Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Ideal Applications</h2>
              <p className="text-lg text-gray-600 mb-8">
                Polymer Shutters excel in environments where moisture resistance is essential. 
                Their durability and easy maintenance make them perfect for both residential and commercial use.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {bestFor.map((item, index) => (
                  <div key={index} className="flex items-center gap-3 bg-white p-4 rounded-xl shadow-sm">
                    <Droplets className="h-5 w-5" style={{color: '#8CB89F'}} />
                    <span className="text-gray-700 font-medium">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/b91ea611-2364-4d55-8415-0e2be2797319/images/128a6a6620e3f91b5c428df7e5edbce10d4f071a1b206bcb32d695f1a1f3a456.png"
                alt="Polymer Shutters in Bathroom"
                className="w-full h-auto rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-xl text-gray-600">
              Common questions about our Polymer Shutters range
            </p>
          </div>
          
          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <Card key={index} className="border-0 shadow-lg">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">{faq.question}</h3>
                  <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 text-white" style={{backgroundColor: '#8CB89F'}}>
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Perfect for Wet Environments</h2>
          <p className="text-xl mb-8 opacity-90">
            Get an instant estimate for Polymer Shutters using our online calculator.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/calculator">
              <Button size="lg" className="bg-white hover:bg-gray-100 px-8 py-4 text-lg" style={{color: '#8CB89F'}}>
                <Calculator className="mr-2 h-5 w-5" />
                Use Our Online Estimator
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/materials">
              <Button 
                size="lg" 
                variant="outline" 
                className="border-white text-white hover:bg-white/10 px-8 py-4 text-lg"
              >
                Compare All Ranges
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PolymerPage;
